<script setup lang="ts">
import {computed, inject, nextTick, reactive, ref, toRaw, useTemplateRef, watch} from "vue";
import {Avatar, Button, ImportantInfoPanel, Menu} from "@/Components/Library";
import {Comments, PageLoading, PageTitle} from "@/Components/Global";
import {useRouter, onBeforeRouteLeave, onBeforeRouteUpdate} from "vue-router";
import {getGroupQuestion, getGroupQuestions, updateParams} from "@/Services/group-questions.ts";
import {getGroup} from "@/Services/groups.ts";
import {
    deleteGroupQuestionResponse,
    getGroupQuestionResponse,
    getGroupQuestionResponses,
    storeGroupQuestionResponse,
    updateGroupQuestionResponse,
} from "@/Services/group-question-responses.ts";
import {getting, saving} from "@/Composables/api.ts";
import {useToast} from "primevue/usetoast";
import useEcho from "@/Composables/echo.ts";
import {onLongPress, useStorage, breakpointsTailwind, useBreakpoints} from "@vueuse/core";
import GroupDrawer from "@/Pages/User/Groups/GroupDrawer.vue";
import QuestionResponsesTable from "@/Pages/User/Questions/Question/Responses/QuestionResponsesTable.vue";
import {useMainStore} from "@/Stores/store.ts";
import QuestionResponsesChart from "@/Pages/User/Questions/Question/Responses/QuestionResponsesChart.vue";
import QuestionResponsesWordCloud from "@/Pages/User/Questions/Question/Responses/QuestionResponsesWordCloud.vue";
import QuestionResponsesVoting from "@/Pages/User/Questions/Question/Responses/QuestionResponsesVoting.vue";
import {Icon} from "@iconify/vue";
import Question from "@/Models/Question.ts";
import User from "@/Models/User.ts";
import {useConfirm} from "primevue/useconfirm";
import {getComment, getQuestionComments} from "@/Services/comments.ts";
import {getReactions} from "@/Services/reactions.ts";
import Str from "@/Utils/Str.ts";
import QuestionEditParamsDrawer from "@/Pages/User/Questions/Question/QuestionEditParamsDrawer.vue";
import ResponseForm from "@/Pages/User/Questions/Question/ResponseForm.vue";
import ResizableColumns from "@/Components/Global/ResizableColumns.vue";
import StimulusBlock from "@/Pages/User/Questions/Question/StimulusBlock.vue";
import markdownit from "markdown-it";
import useGatekeeper from "@/Composables/gatekeeper.ts";
import useSortedQuestions from "@/Composables/sortedQuestions.ts";

const $store = useMainStore();
const $router = useRouter();
const $route = $router.currentRoute;
const toast = useToast();
const $user = new User();
const confirm = useConfirm();

const {sortQuestions} = useSortedQuestions();
const breakpoints = useBreakpoints(breakpointsTailwind);
const state = reactive({
    show: {
        group: null,
        question: null,
        responseForm: false,
        editParams: false,
    },
});
const question = computed<Question | null>({
    get: () => $store.question,
    set: (val) => ($store.question = new Question(val)),
});
const md = markdownit();
const defaultResponse = {id: null, data: null};
const response = useStorage<{[key: string]: any}>(`a${$user.id}:Q:${$route.value.params.question}`, defaultResponse);
if (!response.value) {
    response.value = defaultResponse;
}
const confirmLeaveWithUnsavedChanges = () => {
    let hasData;

    switch (question.value?.type?.slug) {
        case "ranking":
            hasData = JSON.stringify(response.value.data) !== JSON.stringify(question.value.options.map((O) => O.id));
            break;
        case "weighting":
            hasData = Object.values(response.value.data ?? {}).some((value) => +value !== 0);
            break;
        case "text":
        case "annotation":
            hasData = response.value.data?.content;
            break;
        case "multiple_choice":
            hasData = question.value.param("matrix")
                ? Object.values<string[]>(response.value.data ?? {}).some((categories) => categories.length > 0)
                : Array.isArray(response.value.data)
                  ? response.value.data.length > 0
                  : !!response.value.data;
            break;
        default:
            hasData = Array.isArray(response.value.data) ? response.value.data.length > 0 : !!response.value.data;
            break;
    }

    if (!!hasData) {
        const c = window.confirm("You have unsaved changes. Are you sure you want to leave this page?");
        if (!c) {
            return false;
        }
    }
};

onBeforeRouteLeave(() => confirmLeaveWithUnsavedChanges());

onBeforeRouteUpdate((to, from) => {
    if (to.params.question !== from.params.question) {
        return confirmLeaveWithUnsavedChanges();
    }
});
const ownResponses = computed(() => question.value?.responses?.filter((A: any) => A.user_id === $user.id));
const showResponseForm = computed(() => state.show.responseForm || !ownResponses.value.length);

// const {isBlockingGatekeeper} = useGatekeeper(question.value, ownResponses.value);
const isBlockingGatekeeper = computed(() => {
    if (!question.value?.is("gatekeeper")) {
        return false;
    }

    if (!ownResponses.value.length) {
        return true;
    }

    const responseData = ownResponses.value[0].data;
    let selectedIds: string[] = Array.isArray(responseData) ? responseData : [responseData];

    const positiveOptionIds = question.value.options
        .filter((opt: any) => opt.params?.gatekeeper_positive === true)
        .map((opt: any) => String(opt.id));

    if (!positiveOptionIds.length) {
        return false;
    }

    const matches = selectedIds.filter((id) => positiveOptionIds.includes(id));

    if (question.value.param("gatekeeper_all", true)) {
        return matches.length !== positiveOptionIds.length;
    }

    return matches.length === 0;
});

const revealsQuestions = (data: any): boolean => {
    if (!question.value?.is("multiple_choice")) {
        return false;
    }

    const selectedIds = (Array.isArray(data) ? data : [data]).map(String);

    return question.value.options.some(
        (option: any) => option.params?.reveal_tags?.length && selectedIds.includes(String(option.id)),
    );
};

const hasRevealOptions = (): boolean => {
    if (!question.value?.is("multiple_choice")) {
        return false;
    }

    return question.value.options.some((option: any) => option.params?.reveal_tags?.length);
};

const ogMeta = ref<{[key: string]: any}>();
const resetResponse = (e: any = null) => {
    console.log(question);
    response.value.id = null;
    switch (question.value.type.slug) {
        case "gatekeeper":
        case "multiple_choice":
            response.value.data = question.value.param("matrix")
                ? {}
                : question.value.param("allow_multiple")
                  ? []
                  : null;
            break;
        case "word":
            response.value.data = question.value.param("allow_multiple") ? [""] : null;
            break;
        case "ranking":
            response.value.data = question.value.options.map((O) => O.id);
            break;
        case "weighting":
            let opts = new Map(question.value.options.map((O) => [O.id, 0]));
            response.value.data = Object.fromEntries(opts);
            break;
        case "text":
            response.value.data = {
                votes: {},
                content: "",
                audio: null,
            };
            break;
        default:
            response.value.data = null;
            break;
    }
};
const getQuestion = async () => {
    question.value = null;
    try {
        const resp = await getGroupQuestion($route.value.params.group, $route.value.params.question, {}, false);
        question.value = resp.data;
        markViewedPostInFeeds(resp.data.group);

        if (response.value?.data === null) {
        }
        resetResponse();

        sort.value = question.value?.param("allow_annotations") && question.value?.pageCount > 1 ? "page" : "votes";

        console.log(question.value);
    } catch (e: unknown) {
        console.log(e);
        if ([403, 404].includes(e?.response?.status)) {
            await $router.push({name: "feed", params: {group: $route.value.params.group}});
            toast.add({
                severity: "error",
                summary: "Error",
                detail: e?.response?.data?.message ?? "This question is no longer available",
                life: 5000,
            });
        }
    }
};

const markViewedPostInFeeds = (viewedGroup: any): void => {
    if (!question.value?.is("post")) {
        return;
    }

    const groupQuestionId = question.value.get("group_question_id");
    for (const feedQuestion of [...$store.feed.questions, ...$store.group.questions]) {
        if (feedQuestion.group_question_id === groupQuestionId) {
            feedQuestion.viewed_by_current_user = true;
        }
    }

    const viewedMember = viewedGroup?.members?.find((member: any) => member?.id === $store.user?.id);
    if (!viewedMember) {
        return;
    }

    for (const group of [$store.group, ...$store.groups]) {
        if (group?.id !== viewedGroup.id) {
            continue;
        }

        const member = group.members?.find((groupMember: any) => groupMember?.id === $store.user?.id);
        if (member) {
            member.answered_count = viewedMember.answered_count;
        }

        group.member_answer_progress_counts = viewedGroup.member_answer_progress_counts;
    }
};

const isLinearFlow = computed(() => !!$store.group?.params?.linear_flow && !$store.group?.can_admin);
const navigationQuestions = computed(() => {
    if (isLinearFlow.value) {
        return [...$store.group.questions].sort((a, b) => b.order_column - a.order_column);
    }
    return sortQuestions($store.group.questions);
});

const hasNextQuestion = computed(() => {
    if (isBlockingGatekeeper.value) {
        return false;
    }

    if (!$store.group?.questions?.length) {
        return false;
    }

    const currentIndex = navigationQuestions.value.findIndex((q) => q.id === question.value?.id);
    if (currentIndex === -1) {
        return true;
    }

    return currentIndex !== -1 && currentIndex < navigationQuestions.value.length - 1;
});

const hasPreviousQuestion = computed(() => {
    if (!$store.group?.questions?.length) {
        return false;
    }

    const currentIndex = navigationQuestions.value.findIndex((q) => q.id === question.value?.id);
    return currentIndex !== -1 && currentIndex > 0;
});

const nextQuestion = (): void => {
    if (!$store.group?.questions?.length) {
        return;
    }

    const currentIndex = navigationQuestions.value.findIndex((q) => q.id === question.value?.id);
    let nextIndex = currentIndex + 1;

    if (currentIndex === -1) {
        nextIndex = 0;
    }

    if (nextIndex < navigationQuestions.value.length) {
        $router.push({
            name: "groups.questions.show",
            params: {
                group: $route.value.params.group,
                question: navigationQuestions.value[nextIndex].id,
            },
        });
    }
};

const previousQuestion = (): void => {
    if (!$store.group?.questions?.length) {
        return;
    }

    const currentIndex = navigationQuestions.value.findIndex((q) => q.id === question.value?.id);

    if (currentIndex !== -1 && currentIndex > 0) {
        $router.push({
            name: "groups.questions.show",
            params: {
                group: $route.value.params.group,
                question: navigationQuestions.value[currentIndex - 1].id,
            },
        });
    }
};

const loadGroupQuestions = async () => {
    if ($route.value.params.group !== $store.group?.id) {
        try {
            const {data} = await getGroup($route.value.params.group);
            Object.assign($store.group, data);
        } catch (e) {
            console.error("Failed to load group:", e);
        }
    }

    if (!$store.group?.questions?.length) {
        try {
            const {data} = await getGroupQuestions($route.value.params.group);
            $store.group.questions = data;
        } catch (e) {
            console.error("Failed to load questions:", e);
        }
    }
};

loadGroupQuestions();
getQuestion();

const $date = inject("date");

const wordcloud = useTemplateRef("wordcloud");
const questionComments = useTemplateRef("questionComments");

const checkResponse = () => {
    // console.log("checkResponse called");
    if (question.value?.is("voice")) {
        responseComponent.value?.stopRecording();
    }
    if (question.value?.is("text") && question.value.param("allow_annotations") && !response.value.data.annotation) {
        alert("Please anchor your comment to the document");
        // confirm.require({
        //     message: "Are you sure you don’t want to anchor this response to a place in the document?",
        //     header: "Confirmation",
        //     acceptLabel: "Yes",
        //     acceptClass: "bg-askr-green text-white px-2 py-1 rounded-lg hover:brightness-110",
        //     rejectLabel: "No",
        //     rejectClass: "px-2 py-1 rounded-lg hover:brightness-110",
        //     accept: () => saveResponse(),
        // });
        return;
    }

    saveResponse();
};

const validateResponse = () => {
    // console.log("validateResponse called", response.value.data);
    if (question.value?.is("text") && !response.value.data.audio && !response.value.data.content.trim()) {
        toast.add({
            severity: "warn",
            summary: "Invalid response",
            detail: `Please enter ${question.value?.param("allow_voice_responses") ? "or record " : ""}a response before submitting`,
            life: 5000,
        });
        return false;
    }

    if (question.value?.is("word")) {
        const isEmpty = question.value.param("allow_multiple")
            ? Array.isArray(response.value.data) && response.value.data.every((word) => !word || !word.trim())
            : !response.value.data || !response.value.data.trim();

        if (isEmpty) {
            toast.add({
                severity: "warn",
                summary: "Invalid response",
                detail: "Please enter a response before submitting",
                life: 5000,
            });
            return false;
        }
    }

    if (question.value?.is(["multiple_choice", "gatekeeper"])) {
        let isEmpty;
        if (question.value.param("matrix")) {
            const selectedCategoryIds = Object.values(response.value.data ?? {}).flat();
            isEmpty = !(question.value.categories ?? []).every(
                (category) => selectedCategoryIds.includes(category.id),
            );
        } else {
            isEmpty = question.value.param("allow_multiple")
                ? !Array.isArray(response.value.data) || !response.value.data.length
                : !response.value.data;
        }

        if (isEmpty) {
            toast.add({
                severity: "warn",
                summary: "Invalid response",
                detail: "Please select at least one option before submitting",
                life: 5000,
            });
            return false;
        }
    }

    if (question.value?.is("voice")) {
        if (!response.value.data || !(response.value.data instanceof Blob || response.value.data instanceof File)) {
            toast.add({
                severity: "warn",
                summary: "Invalid response",
                detail: "Please record a voice response before submitting",
                life: 5000,
            });
            return false;
        }
    }

    if (question.value?.is("weighting")) {
        const totalWeights = Object.values(response.value.data).reduce((a: any, b: any) => +a + +b, 0);

        if (totalWeights !== 100) {
            toast.add({
                severity: "warn",
                summary: "Invalid response",
                detail: "Please allocate exactly 100 points",
                life: 5000,
            });
            return false;
        }
    }

    return true;
};

const saveComment = async () => {
    if (!!questionComments.value?.comment?.text.trim() || !!questionComments.value?.comment?.audioFile) {
        await questionComments.value.postComment();
    }
};

const updateMemberAnsweredCount = (groupId: string, delta: number) => {
    const apply = (groupLike: any) => {
        const member = groupLike?.members?.find((M: any) => M.id === $store.user?.id);

        if (!member) {
            return;
        }

        member.answered_count = Math.max(0, +(member.answered_count ?? 0) + delta);
    };

    apply($store.group?.id === groupId ? $store.group : null);
    apply($store.groups.find((G: any) => G.id === groupId));
};

const saveResponse = async () => {
    console.log("saveResponse called");
    if (!validateResponse()) {
        console.log("Validation failed");
        return;
    }

    try {
        let res;
        const hadOwnResponsesBefore = (ownResponses.value?.length ?? 0) > 0;

        if (!!response.value.id) {
            const revealsOnUpdate = question.value.is("gatekeeper") || hasRevealOptions();
            res = await updateGroupQuestionResponse($route.value.params.group, question.value.id, response.value);
            state.show.responseForm = false;
            const A = question.value.response(res.id);
            Object.assign(A, res);
            toast.add({
                severity: "success",
                summary: "Success",
                detail: "Your response has been saved",
                life: 5000,
            });

            if ($store.group?.id === $route.value.params.group) {
                let currentResponse = $store.group?.questions
                    ?.find((Q: any) => Q.id === question.value.id)
                    ?.responses?.find((A: any) => A.id === response.value.id);
                if (currentResponse) {
                    Object.assign(currentResponse, res);
                }
            }

            let feedResponse = $store.feed?.questions
                ?.find((Q: any) => Q.id === question.value.id)
                ?.responses?.find((A: any) => A.id === response.value.id);
            if (feedResponse) {
                Object.assign(feedResponse, res);
            }
            resetResponse();

            await saveComment();

            if (revealsOnUpdate) {
                const {data} = await getGroupQuestions($route.value.params.group);
                $store.group.questions = data;
            }

            return;
        }

        res = await storeGroupQuestionResponse(
            $route.value.params.group,
            $route.value.params.question,
            question.value.id,
            {
                data: response.value.data,
            },
        );

        if (question.value.is("gatekeeper") || revealsQuestions(response.value.data) || isLinearFlow.value) {
            const {data} = await getGroupQuestions($route.value.params.group);
            if (data.length > $store.group.questions.length) {
                toast.add({
                    severity: "success",
                    summary: "Questions Unlocked",
                    detail: "New questions have been revealed!",
                    life: 5000,
                });
            }
            $store.group.questions = data;
            await nextTick();
        }

        if ($store.group?.id === $route.value.params.group) {
            let groupQuestion = $store.group?.questions?.find((Q) => Q.id === question.value.id);

            if (groupQuestion) {
                groupQuestion.responses_count++;
                groupQuestion.group_responses_count++;
                groupQuestion.user_responses?.unshift(res);
            }
        }
        let feedQuestion = $store.feed?.questions?.find((Q) => Q.id === question.value.id);
        if (feedQuestion) {
            feedQuestion.responses_count++;
            feedQuestion.group_responses_count++;
            feedQuestion.user_responses?.unshift(res);
        }

        if (!hadOwnResponsesBefore) {
            updateMemberAnsweredCount($route.value.params.group as string, 1);
        }

        state.show.responseForm = false;
        let responseRes = res;

        // Handle audio upload for voice responses or text responses with audio
        const audioFile = question.value.is("voice") ? response.value.data : response.value.data?.audio;

        if (audioFile) {
            try {
                const formData = new FormData();
                formData.append(
                    "files[]",
                    audioFile,
                    Math.random().toString(36).slice(2, 20) + Math.random().toString(36).slice(2, 20) + ".mp3",
                );
                const {data} = await axios.post(
                    route("media.store", {
                        modelType: "response",
                        modelId: res.id,
                        collectionName: "audio",
                    }),
                    formData,
                    {
                        headers: {
                            "Content-Type": "multipart/form-data",
                        },
                    },
                );
                responseRes.files = data.data;
            } catch (e) {
                // console.log(e);
            }
        }
        annotation.value = null;
        // Object.assign(response.value, responseRes);
        question.value.addResponse(responseRes);
        toast.add({
            severity: "success",
            summary: "Success",
            detail: "Your response has been saved",
            life: 5000,
        });
        clearPointsFocus();
        resetResponse();

        await saveComment();
    } catch (e) {
        // console.log(e);
    }

    // console.log(response.value.id);
    //
    // !!response.value.id
    //     ? updateGroupQuestionResponse($route.value.params.group, $route.value.params.question, question.value.id, response.value)
    //           .then((res) => {
    //               state.show.responseForm = false;
    //               const A = question.value.response(res.id);
    //               Object.assign(A, res);
    //               toast.add({
    //                   severity: "success",
    //                   summary: "Success",
    //                   detail: "Your response has been saved",
    //                   life: 5000,
    //               });
    //           })
    //           .catch(resetResponse)
    //     : storeGroupQuestionResponse($route.value.params.group, $route.value.params.question, question.value.id, {
    //           data: response.value.data,
    //       }).then(async (res) => {
    //       });
};
const syncReaction = async (e) => {
    // console.log(e);
    if (e.question !== $router.currentRoute.value.params.question) {
        return;
    }

    await getReactions($router.currentRoute.value.params.group, e.comment).then((data: {reactions: any}) => {
        // console.log(data);
        // let existingComment = reply_to
        //     ? commentsRef.value.find((c) => c.id === reply_to)?.replies.find((r) => r.id === comment)
        //     : commentsRef.value.find((c) => c.id === comment);
        // Object.assign(existingComment, {reactions: data.reactions});
        let comment;
        switch (e.commentable.type) {
            case "App\\Models\\Question":
                comment = question.value.comment(e.comment);
                break;
            case "App\\Models\\Response":
                let response = question.value.response(e.commentable.id);
                comment = response.comments.find((C: any) => C.id === e.comment);
                break;
            case "App\\Models\\Comment":
                // console.log(e);
                let parent;
                switch (e.commentable.parent.type) {
                    case "App\\Models\\Question":
                        parent = question.value.comment(e.commentable.id);
                        break;
                    case "App\\Models\\Response":
                        let response = question.value.response(e.commentable.parent.id);
                        parent = response.comments.find((C: any) => C.id === e.commentable.id);
                        break;
                }
                // console.log(parent);
                if (parent) {
                    comment = parent.replies.find((C: any) => C.id === e.comment);
                }
        }
        // console.log(comment);
        if (comment) {
            Object.assign(comment, {reactions: data.reactions});
        }
    });
};
useEcho([
    {
        event: "GroupQuestions\\GroupQuestionParamsUpdated",
        callback: async (e: {group: string; question: {id: string; params: any}}) => {
            if (e.group === $route.value.params.group && e.question.id === question.value.id) {
                if (question.value.isOwner($user)) {
                    return;
                }

                for (const key of Object.keys(e.question.params)) {
                    switch (key) {
                        case "show_charts":
                            break;
                        case "show_responses":
                            if (e.question.params.show_responses && !question.value.param("show_responses")) {
                                const {data} = await getGroupQuestionResponses(
                                    $route.value.params.group,
                                    $route.value.params.question,
                                    {
                                        include: [
                                            "files",
                                            "user.profile.avatars",
                                            "comments.commentator.profile.avatars",
                                            "comments.reactions",
                                            "comments.comments.commentator.profile.avatars",
                                        ],
                                    },
                                );
                                question.value.setResponses(data);
                            } else if (!e.question.params.show_responses && question.value.param("show_responses")) {
                                question.value.setResponses(
                                    question.value.responses.filter((A) => A.user_id === $user.id),
                                );
                            }
                            break;
                        case "allow_comments":
                            //todo if allow_comments is set to false and question allow comments is true
                            // we need to remove all comments from the question and hide the panel
                            if (e.question.params.allow_comments && !question.value.param("allow_comments")) {
                                const {data} = await getQuestionComments($route.value.params.question, {
                                    filter: {groups: [$route.value.params.group]},
                                });
                                question.value.setComments(data);
                            }
                            if (!e.question.params.allow_comments && question.value.param("allow_comments")) {
                                question.value.setComments([]);
                            }
                            break;
                        default:
                            break;
                    }
                    question.value.param(key, e.question.params[key]);
                }
            }
        },
    },
    {
        event: "Responses\\ResponseCreated",
        callback: async (e: {response: string; question: string}) => {
            if (e.question === question.value.id) {
                const exists = question.value.response(e.response);
                if (!exists) {
                    const data = await getGroupQuestionResponse(
                        $route.value.params.group,
                        $route.value.params.question,
                        e.response,
                    );
                    question.value.responses.unshift(data);
                }
            }
        },
    },
    {
        event: "Responses\\ResponseUpdated",
        callback: async (e: {response: string; question: string}) => {
            if (e.question === question.value.id) {
                const data = await getGroupQuestionResponse(
                    $route.value.params.group,
                    $route.value.params.question,
                    e.response,
                );
                const A = question.value.response(e.response);
                Object.assign(A, data);
            }
        },
    },
    {
        event: "Comments\\CommentCreated",
        callback: async (e: {question: string; comment: string}) => {
            // console.log("comment created", e);
            if (e.question !== $router.currentRoute.value.params.question) {
                return;
            }

            await getComment($router.currentRoute.value.params.group, e.comment).then(({data}) => {
                // console.log("comment data", data);
                switch (data.commentable_type) {
                    case "App\\Models\\Question":
                        question.value.addComment(data);
                        break;
                    case "App\\Models\\Response":
                        let response = question.value.response(data.commentable_id);
                        // console.log(response);
                        response.comments_count++;
                        response.comments.unshift(data);
                        break;
                    case "App\\Models\\Comment":
                        // console.log(e.commentable.parent);
                        let comment;
                        switch (e.commentable.parent.type) {
                            case "App\\Models\\Question":
                                comment = question.value.comment(e.commentable.id);
                                // console.log(comment);
                                break;
                            case "App\\Models\\Response":
                                let response = question.value.response(e.commentable.parent.id);
                                comment = response.comments.find((C) => C.id === e.commentable.id);
                                // console.log(response, comment);
                                break;
                        }
                        if (comment) {
                            comment.replies.unshift(data);
                        }
                }
            });
        },
    },
    {
        event: "Comments\\CommentUpdated",
        callback: async (e: {question: string; comment: string; reply_to: string | null}) => {
            if (e.question !== $router.currentRoute.value.params.question) {
                return;
            }

            await getComment($router.currentRoute.value.params.group, e.comment).then(({data}: {data: any}) => {
                // console.log("comment data", data);
                let comment;
                switch (data.commentable_type) {
                    case "App\\Models\\Question":
                        comment = question.value.comment(data.id);
                        break;
                    case "App\\Models\\Response":
                        let response = question.value.response(data.commentable_id);
                        comment = response.comments.find((C: any) => C.id === data.id);
                        break;
                    case "App\\Models\\Comment":
                        // console.log(e.commentable.parent);
                        let parent;
                        switch (e.commentable.parent.type) {
                            case "App\\Models\\Question":
                                parent = question.value.comment(e.commentable.id);
                                break;
                            case "App\\Models\\Response":
                                let response = question.value.response(e.commentable.parent.id);
                                parent = response.comments.find((C: any) => C.id === e.commentable.id);
                                break;
                        }
                        if (parent) {
                            comment = parent.replies.find((C: any) => C.id === data.id);
                        }
                }
                if (comment) {
                    Object.assign(comment, data);
                }
            });
        },
    },
    {
        event: "Comments\\CommentDeleted",
        callback: async (e: {question: string; comment: string; reply_to: string | null}) => {
            if (e.question !== $router.currentRoute.value.params.question) {
                return;
            }

            // console.log(e);

            switch (e.commentable.type) {
                case "App\\Models\\Question":
                    question.value.removeComment(e.comment);
                    break;
                case "App\\Models\\Response":
                    let response = question.value.response(e.commentable.id);
                    if (response) {
                        response.comments = response.comments.filter((C) => C.id !== e.comment);
                        response.comments_count--;
                    }
                    break;
                case "App\\Models\\Comment":
                    // console.log(e.commentable.parent);
                    let parent;
                    switch (e.commentable.parent.type) {
                        case "App\\Models\\Question":
                            parent = question.value.comment(e.commentable.id);
                            break;
                        case "App\\Models\\Response":
                            let response = question.value.response(e.commentable.parent.id);
                            parent = response.comments.find((C) => C.id === e.commentable.id);
                            break;
                    }
                    if (parent) {
                        parent.replies = parent.replies.filter((C) => C.id !== e.comment);
                    }
            }
        },
    },
    //TODO add checks for current question
    {
        event: "Reactions\\ReactionCreated",
        callback: async (e: {question: string; comment: string; reply_to: string | null}) => await syncReaction(e),
    },
    {
        event: "Reactions\\ReactionDeleted",
        callback: async (e: {question: string; comment: string; reply_to: string | null}) => await syncReaction(e),
    },
]);
const vote = (response) => (question.value.response(response.id).data.votes = response.data.votes);

const updateVotes = ({response, data}) => (question.value.response(response).data = data);

const isSaved = computed(() => {
    const ownResponse = ownResponses.value.find((A) => A.id === response.value?.id);
    return JSON.stringify(response.value?.data) === JSON.stringify(ownResponse?.data);
});
const points = computed(() =>
    question.value.responses.filter((A) => A.data?.annotation).map((A) => A.data?.annotation),
);
const responseComponent = useTemplateRef("responseComponent");
const responseBlock = useTemplateRef("responseBlock");
const stimulusBlock = useTemplateRef("stimulusBlock");
const stimulusBlockSm = useTemplateRef("stimulusBlockSm");
// stimulusDisplay is now accessed through stimulusBlock.value.stimulusDisplay
const responsesTable = useTemplateRef("responsesTable");
const annotation = ref();
const pageIndex = ref(0);
const stimulusClicked = async (event) => {
    if (
        !["image", "slides"].includes(question.value.stimuli?.[0].type.slug) ||
        !question.value.is("text") ||
        !question.value.param("allow_annotations")
    ) {
        return;
    }
    // console.log(stimulusBlock.value.stimulusDisplay.activeIndex, question.value.stimuli?.[0]?.type);
    if (ownResponses.value.length && !question.value.param("allow_multiple")) {
        alert("You have already responded to this question. Multiple responses are not enabled.");
        return;
    }
    if (question.value.type.slug === "text" && !question.value.param("allow_annotations")) {
        alert("Annotations are not enabled for this question.");
        return;
    }
    // console.log(ownResponses.value, question.value.params.allow_multiple);
    // if (annotation.value) {
    //     points.value = points.value.filter((A) => A.id !== annotation.value.id);
    // }
    points.value.forEach((A) => (A.focussed = false));
    const x = event.offsetX;
    const y = event.offsetY - 18;

    const width = event.target.width;
    const height = event.target.height;

    const percentageX = (x / width) * 100;
    const percentageY = (y / height) * 100;
    // console.log("stimulus clicked", event);
    let annotationId = `u${$user.id}-a${points.value.length}`;
    annotation.value = {
        x: percentageX,
        y: percentageY,
        id: annotationId,
        focussed: true,
        color: $user.profile.color?.class,
        pageIndex:
            question.value.stimuli?.[0]?.type.slug === "slides" ? stimulusBlock.value.stimulusDisplay.activeIndex : 0,
    };
    response.value.data.annotation = annotation.value;
    // console.log(response.value.data);
    state.show.responseForm = true;
    await nextTick();
    responseBlock.value.scrollIntoView({behavior: "smooth", block: "nearest"});
    // console.log(responseBlock.value);
    setTimeout(() => responseBlock.value.querySelector("textarea")?.focus(), 500);
};
onLongPress(stimulusBlock, stimulusClicked, {
    delay: 500,
    // onMouseUp: (duration, distance, isLongPress) => {
    //
    // },
});

const responseClick = async (id) => {
    if (!["image", "slides"].includes(question.value.stimuli?.[0].type.slug) || !question.value.is("text")) {
        return;
    }
    const response = question.value.response(id);
    if (!response.data.annotation) {
        return;
    }
    pageIndex.value = +response.data.annotation?.pageIndex;
    let block = breakpoints.greaterOrEqual("xl").value ? stimulusBlock : stimulusBlockSm;
    block.value.stimulusDisplay.swiper?.swiper.slideTo(+response.data.annotation.pageIndex);
    // console.log(id, response, pageIndex.value, stimulusBlock.value.stimulusDisplay);
    points.value.forEach((A) => (A.focussed = false));
    responsesTable.value.responseRefs.forEach((A) => {
        A.content.classList.remove("bg-askr-green-50/20", "border-askr-green", "dark:border-askr-green");
        A.content.classList.add("dark:border-stone-700");
    });
    // console.log(breakpoints.greaterOrEqual("xl"), block);
    setTimeout(() => {
        const point = block.value.stimulusDisplay.pointRefs?.find((A) => A.id === response.data.annotation.id);
        // console.log(point);
        if (point) {
            const annotation = points.value.find((A) => A.id === response.data.annotation.id);
            annotation.focussed = true;
            point.scrollIntoView({
                behavior: "smooth",
                block: "center",
            });
            const responseContainer = responsesTable.value.responseRefs.find((A) => A.content.id === response.id);
            responseContainer.content.classList.remove("dark:border-stone-700");
            responseContainer.content.classList.add(
                "bg-askr-green-50/20",
                "border-askr-green",
                "dark:border-askr-green",
            );
        }
    }, 0);
};

const pointClick = (point) => {
    // console.log(point, question.value.responses);
    annotation.value = null;
    // responsesTable.value.responseRefs.forEach((A) => console.log(A.content));
    const response = question.value.response(point.id);
    // console.log(response);
    // return;
    if (!response) {
        return;
    }
    points.value.forEach((A) => (A.focussed = false));
    responsesTable.value.responseRefs.forEach((A) => {
        A.content.classList.remove("bg-askr-green-50/20", "border-askr-green", "dark:border-askr-green");
        A.content.classList.add("dark:border-stone-700");
    });
    point.focussed = true;
    // responsesTable.value.responseRefs.forEach((A) => console.log(A.content.id === response.id));
    const responseContainer = responsesTable.value.responseRefs.find((A) => A.content.id === response.id);
    // console.log(responseContainer);
    if (responseContainer?.content) {
        responseContainer.content.scrollIntoView({behavior: "smooth", block: "start"});
        responseContainer.content.classList.remove("dark:border-stone-700");
        responseContainer.content.classList.add("bg-askr-green-50/20", "border-askr-green", "dark:border-askr-green");
    }
    // console.log(
    //     point,
    //     response,
    //     responsesTable.value.responseRefs.find((A) => A.id === `a${response.id}`),
    // );
};

const clearPointsFocus = () => {
    points.value.forEach((A) => (A.focussed = false));
    responsesTable.value?.responseRefs?.forEach((A) => {
        A?.content?.classList?.remove("bg-askr-green-50/20", "border-askr-green", "dark:border-askr-green");
        A.content.classList.add("dark:border-stone-700");
    });
};

const add = () => {
    resetResponse();
    state.show.responseForm = true;
};

const handleEdit = async (editing) => {
    // console.log(editing);
    clearPointsFocus();
    state.show.responseForm = true;

    Object.assign(response.value, structuredClone(toRaw(editing)));

    await nextTick();
    responseBlock.value.scrollIntoView({behavior: "smooth", block: "center"});
};

const handleDelete = async (deleting) => {
    const ownResponsesCount = (question.value?.responses ?? []).filter((A) => A.user_id === $user.id).length;
    const shouldDecrementAnsweredCount = deleting.user_id === $user.id && ownResponsesCount === 1;

    await deleteGroupQuestionResponse($router.currentRoute.value.params.group, question.value.id, deleting.id);
    question.value.question.responses = question.value.question.responses.filter((A) => A.id !== deleting.id);

    if (shouldDecrementAnsweredCount) {
        updateMemberAnsweredCount($route.value.params.group as string, -1);
    }

    // Update store state for the group questions if it exists
    if ($store.group?.questions) {
        const groupQuestion = $store.group.questions.find((q) => q.id === question.value.id);
        if (groupQuestion) {
            groupQuestion.responses = groupQuestion.responses?.filter((r) => r.id !== deleting.id) || [];
            // Also update counts if necessary
            if (groupQuestion.responses_count > 0) groupQuestion.responses_count--;
            if (groupQuestion.user_responses_count > 0) groupQuestion.user_responses_count--;

            if (question.value.is("gatekeeper") || revealsQuestions(deleting.data)) {
                const {data} = await getGroupQuestions($route.value.params.group);
                $store.group.questions = data;
            }
        }
    }

    toast.add({
        severity: "success",
        summary: "Success",
        detail: "Your response has been deleted",
        life: 3000,
    });
    resetResponse();
};

const addResponseComment = ({response, comment}) => {
    response.comments.unshift(comment);
    response.comments_count++;
};

const deleteResponseComment = (e) => {
    console.log(e);
    e.response.comments = e.response.comments.filter((A) => A.id !== e.comment);
    e.response.comments_count--;
};

const sort = ref(question.value?.param("allow_annotations") && question.value?.pageCount > 1 ? "page" : "date");
const sortPanel = useTemplateRef("sortPanel");
const sortByPageMenuItems = {
    label: "By page",
    icon: "mdi:sort-numeric-descending-variant",
    command: () => {
        sort.value = "page";
    },
};
const sortMenuItemsBase = [
    {
        label: "Most votes",
        icon: "mdi:sort-numeric-descending",
        command: () => {
            sort.value = "votes";
        },
    },
    {
        label: "Most comments",
        icon: "mdi:comment-multiple",
        command: () => {
            sort.value = "comments";
        },
    },
    {
        label: "By person",
        icon: "mdi:account",
        command: () => {
            sort.value = "person";
        },
    },
    {
        label: "Recently edited",
        icon: "ic:round-edit-calendar",
        command: () => {
            sort.value = "date";
        },
    },
];
const sortMenuItems = ref([]);

const openSortMenu = (event: any) => {
    if (
        (question.value?.is("text") || question.value?.is("annotation")) &&
        question.value?.param("allow_annotations") &&
        question.value?.pageCount > 1
    ) {
        sortMenuItems.value = [sortByPageMenuItems, ...sortMenuItemsBase];
    } else {
        sortMenuItems.value = sortMenuItemsBase;
    }

    sortPanel.value.menu.toggle(event);
};

watch(() => $route.value.params.question, getQuestion);
const scrollToPageHeading = (index) => {
    // If responsesTable and pageHeadings are available, scroll the corresponding page heading into view
    if (responsesTable.value?.pageHeadings?.[index]) {
        responsesTable.value.pageHeadings[index].scrollIntoView({behavior: "smooth", block: "start"});
    }
};

const handlePageHeadingInView = ({index, isIntersecting, entry, element}) => {
    return;
    // console.log(entry);
    if (isIntersecting) {
        // When heading comes into view, change to that slide
        // console.log(`Page heading ${index + 1} is in view`);
        stimulusBlock.value.stimulusDisplay.swiper?.swiper.slideTo(+index);
    } else {
        // Check if the element is leaving through the bottom (scrolling up)
        if (responseBlock.value) {
            const scrollTop = responseBlock.value.scrollTop;
            const scrollHeight = responseBlock.value.scrollHeight;
            const elementTop = element.offsetTop;
            const elementHeight = element.offsetHeight;

            // Element is leaving through bottom if:
            // 1. It's below the current scroll position (elementTop > scrollTop)
            // 2. But not too far below that it's completely out of view
            // 3. We're scrolling up (can infer from the fact that the element is no longer intersecting)
            const isLeavingThroughBottom =
                elementTop > scrollTop && elementTop < scrollTop + responseBlock.value.clientHeight && index > 0;

            // console.log(`Element ${index + 1} position:`, {
            //     elementTop,
            //     elementHeight,
            //     scrollTop,
            //     scrollHeight,
            //     clientHeight: responseBlock.value.clientHeight,
            //     isLeavingThroughBottom,
            // });

            if (isLeavingThroughBottom) {
                // If we're scrolling up and the heading is leaving through the bottom,
                // show the previous slide
                // console.log(`Page heading ${index + 1} left view (scrolling up), showing page ${index}`);
                stimulusBlock.value.stimulusDisplay.swiper?.swiper.slideTo(+(index - 1));
            }
        }
    }
};

watch(
    () => stimulusBlock.value?.stimulusDisplay?.activeIndex,
    (newIndex) => {
        clearPointsFocus();
        if (newIndex !== undefined && newIndex !== null) {
            scrollToPageHeading(newIndex);
        }
    },
);

const saveParam = async (key: string, label: string, newValue: boolean) => {
    try {
        question.value.param(key, newValue);
        await updateParams($route.value.params.group, $route.value.params.question, {[key]: newValue});
        toast.add({
            severity: "success",
            summary: "Settings Updated",
            detail: `${label} have been ${newValue ? "shown" : "hidden"}`,
            life: 5000,
        });
    } catch (e) {
        console.error(e);
    }
};

const showCharts = computed({
    get: () => !!question.value?.param("show_charts"),
    set: (value: boolean) => saveParam("show_charts", "Charts", value),
});
const showResponses = computed({
    get: () => !!question.value?.param("show_responses"),
    set: (value: boolean) => saveParam("show_responses", "Responses", value),
});
const allowComments = computed({
    get: () => !!question.value?.param("allow_comments"),
    set: (value: boolean) => saveParam("allow_comments", "Comments", value),
});
</script>
<template>
    <div class="m-container -mt--10 mb--8" @click="clearPointsFocus">
        <div class="mb-3" :class="[{'mx-auto max-w-7xl': !question?.stimulus}]">
            <PageTitle title="Respond" :route="{name: 'feed', params: {group: $route.params.group}}">
                <p v-if="!!question?.stimulus" class="ml-auto hidden text-xs text-stone-500 xl:block">
                    Click and drag the dotted line to adjust the column widths
                </p>
            </PageTitle>
        </div>
        <PageLoading v-if="getting.includes('questions/')" class="pt-16" />
        <div v-if="question?.id">
            <ResizableColumns>
                <template #left>
                    <div
                        ref="responseBlock"
                        class="top-4 mx-auto max-w-7xl pr-2"
                        :class="[
                            {
                                'py-2 xl:sticky xl:max-h-[calc(100vh-theme(space.0))] xl:overflow-y-auto':
                                    !!question.stimulus,
                            },
                        ]"
                    >
                        <div class="mb-4">
                            <div class="mb-2 flex w-full grow items-center">
                                <RouterLink
                                    v-if="question.group"
                                    :to="{name: 'feed', params: {group: question.group.id}}"
                                    class="rounded-lg !p-0"
                                >
                                    <div class="mr-auto flex items-center gap-2">
                                        <Avatar
                                            :label="question.group.icon ? undefined : question.group.initials"
                                            :image="question.group.icon?.url"
                                            class="mr--2 h-8 w-8"
                                            :class="[
                                                `bg-${question.group.color?.class}`,
                                                question.group.color?.class === 'white' ? 'text-black' : 'text-white',
                                            ]"
                                        />
                                        <h6 class="text--sm font-bold hover:underline">{{ question.group.name }}</h6>
                                    </div>
                                </RouterLink>
                                <div class="ml-auto flex items-center gap-2">
                                    <Button
                                        v-if="hasPreviousQuestion"
                                        @click="previousQuestion"
                                        class="bg-askr-green !py-1.5 px-4 text-sm text-white hover:brightness-110"
                                        :disabled="!hasPreviousQuestion"
                                    >
                                        <Icon icon="bxs:left-arrow" width="18" class="-ml-1 mr-2" />
                                        Previous
                                    </Button>
                                    <Button
                                        v-if="isBlockingGatekeeper || !hasPreviousQuestion"
                                        @click="$router.push({name: 'feed', params: {group: $route.params.group}})"
                                        class="bg-stone-600 !py-1.5 px-4 text-sm text-white hover:brightness-110 dark:bg-stone-800"
                                    >
                                        <Icon icon="bxs:grid" width="18" class="-ml-1 mr-2" />
                                        Back to Group
                                    </Button>
                                    <Button
                                        v-if="hasNextQuestion"
                                        @click="nextQuestion"
                                        class="bg-askr-green !py-1.5 px-4 text-sm text-white hover:brightness-110"
                                        :disabled="!hasNextQuestion"
                                    >
                                        Next
                                        <Icon icon="bxs:right-arrow" width="18" class="-mr-1 ml-2" />
                                    </Button>
                                    <Button
                                        v-if="question?.group?.can_admin"
                                        :icon="{icon: 'prime:cog', width: '28'}"
                                        @click="state.show.editParams = true"
                                    />
                                </div>
                            </div>
                            <ImportantInfoPanel
                                v-if="question.group.important_info"
                                :content="question.group.important_info"
                                class="mb-4 w-full"
                            />
                            <div class="mb-1 flex flex-wrap items-center">
                                <div
                                    v-if="
                                        question.is('post') ||
                                        question.group?.params.allow_member_posts ||
                                        !question.publish_from
                                    "
                                    class="mr-auto flex items-center gap-0.5"
                                >
                                    <Icon icon="mdi:calendar-outline" width="16" />
                                    <span class="font-medium tracking-tighter lg:tracking-normal">
                                        {{ $date(question.created_at).format("DD/MM/YY HH:mm") }}
                                    </span>
                                </div>
                                <div v-else class="mr-auto flex items-center gap-2 lg:gap-4">
                                    <div class="flex items-center gap-0.5" v-if="question.publish_from">
                                        <Icon icon="mdi:calendar-start-outline" width="16" />
                                        <span class="font-medium tracking-tighter lg:tracking-normal">
                                            {{ $date(question.publish_from).format("DD/MM/YY HH:mm") }}
                                        </span>
                                    </div>
                                    <div class="flex items-center gap-0.5" v-if="question.publish_to">
                                        <Icon icon="mdi:calendar-end-outline" width="16" />
                                        <span class="font-medium tracking-tighter lg:tracking-normal">
                                            {{ $date(question.publish_to).format("DD/MM/YY HH:mm") }}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div
                                v-if="question.is('gatekeeper')"
                                class="mb-2 flex items-center gap-1 text-xs font-bold uppercase text-red-600"
                            >
                                <Icon icon="mdi:gate" width="16" />
                                <span>Gatekeeper</span>
                            </div>
                            <h3
                                class="prose prose-stone mb-2 w-full max-w-full dark:prose-invert"
                                v-html="Str.of(md.render(question.get('title'))).toString()"
                            ></h3>
                            <!--                <TagList class="mt-2" :tags="question.tags" />-->
                        </div>
                        <StimulusBlock
                            class="xl:hidden"
                            ref="stimulusBlockSm"
                            :question="question"
                            :points="points"
                            :annotation="annotation"
                            @pointClick="pointClick"
                        />
                        <template v-if="!question.is('post')">
                            <div v-auto-animate>
                                <template v-if="showResponseForm">
                                    <ResponseForm
                                        v-model="response.data"
                                        :question
                                        :response
                                        :saving="saving.includes('responses')"
                                        :isSaved
                                        @save="checkResponse"
                                        @cancel="
                                            () => {
                                                state.show.responseForm = false;
                                                resetResponse();
                                            }
                                        "
                                    />
                                </template>
                                <template
                                    v-if="!showResponseForm && question.is('text') && question.param('allow_multiple')"
                                >
                                    <div class="mb-8 flex justify-center">
                                        <Button class="w-full bg-stone-800 px-4 py-2 font-bold text-white" @click="add">
                                            Add another response
                                        </Button>
                                    </div>
                                </template>
                            </div>
                            <div v-if="question.is('text')" class="mb-8">
                                <div class="-mb-4 flex items-start justify-between">
                                    <h3 class="font-bold text-stone-500 lg:px-0">
                                        {{ question.param("allow_voting") ? "Vote on responses" : "Responses" }}
                                    </h3>
                                    <Button
                                        :icon="{icon: 'iconoir:sort', width: 26}"
                                        :rounded="false"
                                        class="aspect-square h-full flex-none bg-askr-green !p-2.5 text-white hover:brightness-110"
                                        @click="openSortMenu"
                                    />
                                    <Menu ref="sortPanel" :popup="true" :model="sortMenuItems" />
                                </div>
                                <QuestionResponsesVoting
                                    ref="responsesTable"
                                    class="mb-6 overflow-y-auto"
                                    :question="question"
                                    :sort="sort"
                                    @vote="vote"
                                    @votesChanged="updateVotes"
                                    @responseClick="responseClick"
                                    @edit="handleEdit($event)"
                                    @delete="handleDelete"
                                    @comment="addResponseComment($event)"
                                    @deleteComment="deleteResponseComment($event)"
                                    @pageHeadingInView="handlePageHeadingInView"
                                />
                            </div>
                        </template>
                        <template v-if="question.group.anonymization.slug !== 'invisible'">
                            <template
                                v-if="
                                    question?.group.can_admin ||
                                    (question.param('allow_comments') && !question.is('text'))
                                "
                            >
                                <div class="mb-6">
                                    <div class="mb-2 flex">
                                        <h3 class="font-bold text-stone-500 lg:px-0">Comments</h3>
                                        <Button
                                            v-tooltip:top="
                                                question.param('allow_comments')
                                                    ? 'Disable comments'
                                                    : 'Enable comments'
                                            "
                                            :icon="{
                                                icon: question.param('allow_comments')
                                                    ? 'fluent:eye-24-filled'
                                                    : 'fluent:eye-off-24-regular',
                                                width: 24,
                                            }"
                                            class="ml-2"
                                            v-if="question.group.can_admin"
                                            @click="allowComments = !allowComments"
                                        ></Button>
                                    </div>
                                    <div>
                                        <div
                                            v-if="
                                                question.param('disable_unresponded_comments') && !ownResponses.length
                                            "
                                            class="p-8"
                                        >
                                            <p class="text-center text-stone-500">
                                                You must respond to the question before you can comment.
                                            </p>
                                        </div>
                                        <Comments
                                            ref="questionComments"
                                            :showForm="
                                                !(
                                                    question.param('disable_unresponded_comments') &&
                                                    !ownResponses.length
                                                )
                                            "
                                            :model="question"
                                            :allowVoice="question.param('allow_voice_comments')"
                                            model-type="question"
                                            :highlight-id="$route.query.comment_id"
                                            @comment="question.addComment($event)"
                                            @delete="question.removeComment($event)"
                                            :can-admin="question.group.can_admin"
                                        />
                                    </div>
                                </div>
                            </template>
                            <template
                                v-if="
                                    !question.is(['voice', 'text', 'post']) &&
                                    (question?.group.can_admin || question.param('show_charts'))
                                "
                            >
                                <div class="mb-2 flex">
                                    <h3 class="font-bold text-stone-500 lg:px-0">Charts</h3>
                                    <Button
                                        v-tooltip:top="question.param('show_charts') ? 'Hide charts' : 'Show charts'"
                                        :icon="{
                                            icon: question.param('show_charts')
                                                ? 'fluent:eye-24-filled'
                                                : 'fluent:eye-off-24-regular',
                                            width: 24,
                                        }"
                                        class="ml-2"
                                        v-if="question.group.can_admin"
                                        @click="showCharts = !showCharts"
                                    ></Button>
                                </div>
                                <div class="mb-6">
                                    <QuestionResponsesChart
                                        v-if="
                                            question.is([
                                                'multiple_choice',
                                                'ranking',
                                                'slider',
                                                'weighting',
                                                'gatekeeper',
                                            ])
                                        "
                                        :question="question"
                                    />
                                    <QuestionResponsesWordCloud
                                        ref="wordcloud"
                                        :question="question"
                                        v-if="question.is('word')"
                                    />
                                </div>
                            </template>
                            <template v-if="!question.is(['text', 'post'])">
                                <div class="mb-2 flex">
                                    <h3 class="font-bold text-stone-500 lg:px-0">Responses</h3>
                                    <Button
                                        v-tooltip:top="
                                            question.param('show_responses') ? 'Hide responses' : 'Show responses'
                                        "
                                        :icon="{
                                            icon: question.param('show_responses')
                                                ? 'fluent:eye-24-filled'
                                                : 'fluent:eye-off-24-regular',
                                            width: 24,
                                        }"
                                        class="ml-2"
                                        v-if="question.group.can_admin"
                                        @click="showResponses = !showResponses"
                                    ></Button>
                                </div>
                                <div class="">
                                    <QuestionResponsesTable
                                        @response-click="responseClick"
                                        :question="question"
                                        @edit="handleEdit"
                                        @delete="handleDelete"
                                    />
                                </div>
                            </template>
                        </template>
                    </div>
                </template>
                <template #right v-if="question.stimulus">
                    <StimulusBlock
                        class="hidden xl:block"
                        ref="stimulusBlock"
                        :question="question"
                        :points="points"
                        :annotation="annotation"
                        @pointClick="pointClick"
                    />
                </template>
            </ResizableColumns>
            <!--            <div class="relative w-full gap-3 xl:flex" ref="resizableColumns">-->
            <!--                &lt;!&ndash; Response Block with dynamic width &ndash;&gt;-->
            <!--                -->
            <!--                &lt;!&ndash; Draggable Handle &ndash;&gt;-->
            <!--                <div-->
            <!--                    ref="resizeColumnsDragHandle"-->
            <!--                    class="hidden w-2 cursor-col-resize bg-stone-200 transition-colors hover:bg-askr-green active:bg-askr-green xl:block"-->
            <!--                    :class="{'bg-askr-green': isDragging}"-->
            <!--                ></div>-->
            <!--                &lt;!&ndash; Stimulus Block with dynamic width &ndash;&gt;-->
            <!--                -->
            <!--            </div>-->
            <QuestionEditParamsDrawer v-model:visible="state.show.editParams" :question="question" />
        </div>
        <GroupDrawer :group="state.show.group" @hide="state.show.group = null" />
    </div>
</template>
<style scoped>
.cursor-col-resize {
    cursor: col-resize;
    position: relative;
}

/* Visual indicator for the handle */
.cursor-col-resize::before {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 4px;
    height: 30px;
    background-color: currentColor;
    border-radius: 2px;
}
</style>
